/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { Badge } from 'reactstrap';
import classNames from 'classnames';

type StatusType =
  | 'generating' | 'queued' | 'stuck' | 'error' | 'ready' | 'synced'
  | 'pending' | 'approved' | 'rejected' | 'skipped' | 'flagged';

interface StatusConfig {
  label: string;
  badgeColor: string;
  borderColor: string;
  animated?: boolean;
}

const STATUS_CONFIG: Record<StatusType, StatusConfig> = {
  generating: { label: 'Generating', badgeColor: 'warning', borderColor: 'var(--theme-WarningMedium)', animated: true },
  queued: { label: 'Waiting to Start', badgeColor: 'secondary', borderColor: 'transparent' },
  stuck: { label: 'Stuck', badgeColor: 'danger', borderColor: 'var(--theme-DangerMedium)' },
  error: { label: 'Error', badgeColor: 'danger', borderColor: 'var(--theme-DangerMedium)' },
  ready: { label: 'Ready', badgeColor: 'info', borderColor: 'var(--theme-BgInfoMedium)' },
  synced: { label: 'Synced', badgeColor: 'success', borderColor: 'var(--theme-SuccessMedium)' },
  pending: { label: 'Pending', badgeColor: 'info', borderColor: 'var(--theme-BgInfoMedium)' },
  approved: { label: 'Approved', badgeColor: 'success', borderColor: 'var(--theme-SuccessMedium)' },
  rejected: { label: 'Rejected', badgeColor: 'danger', borderColor: 'var(--theme-DangerMedium)' },
  skipped: { label: 'Skipped', badgeColor: 'secondary', borderColor: 'transparent' },
  flagged: { label: 'Flagged', badgeColor: 'warning', borderColor: 'transparent' },
};

export const getStatusConfig = (status: string): StatusConfig =>
  STATUS_CONFIG[status as StatusType] ?? STATUS_CONFIG.pending;

export const getRowBorderColor = (status: string): string =>
  getStatusConfig(status).borderColor;

interface StatusBadgeProps {
  status: string;
  className?: string;
}

export const StatusBadge = ({ status, className }: StatusBadgeProps) => {
  const config = getStatusConfig(status);
  return (
    <Badge color={config.badgeColor} pill className={classNames('fw-normal', className)}>
      {config.animated && <i className='fas fa-spinner fa-spin me-1' />}
      {config.label}
    </Badge>
  );
};
